import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, User, Crown, ChevronDown, ChevronRight, Plus, X, Search, MoreVertical, Edit2, Trash2, Heart, Activity, FileText, Pill, Calendar, AlertCircle, Users as UsersIcon, Shield, Eye, EyeOff, Upload, Download, Filter, Bell, Target, TrendingUp, Phone, Droplet, Ruler, Scale, Apple } from 'lucide-react';
import { fetchHealthRecords, createHealthRecord, updateHealthRecord, deleteHealthRecord } from '../services/healthRecordsApi';
import MemberAvatar from './MemberAvatar';
import UserAvatar from './UserAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';

const HealthWellness = () => {
    const navigate = useNavigate();
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [activeTab, setActiveTab] = useState('overview'); // overview, records, medications, visits, fitness, emergency
    const [selectedMember, setSelectedMember] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showFirstAidModal, setShowFirstAidModal] = useState(false);
    const [modalType, setModalType] = useState(''); // 'basicInfo', 'record', 'medication', 'visit', 'goal', 'emergency'
    const [searchQuery, setSearchQuery] = useState('');
    const [filterTag, setFilterTag] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    
    // Health data state
    const [healthData, setHealthData] = useState({});
    
    // Form states
    const [bloodGroup, setBloodGroup] = useState('');
    const [height, setHeight] = useState('');
    const [weight, setWeight] = useState('');
    const [allergies, setAllergies] = useState('');
    const [recordTitle, setRecordTitle] = useState('');
    const [recordFile, setRecordFile] = useState(null);
    const [recordDate, setRecordDate] = useState('');
    const [recordDoctor, setRecordDoctor] = useState('');
    const [recordTags, setRecordTags] = useState('');
    const [medName, setMedName] = useState('');
    const [medDosage, setMedDosage] = useState('');
    const [medSchedule, setMedSchedule] = useState('');
    const [medStartDate, setMedStartDate] = useState('');
    const [medEndDate, setMedEndDate] = useState('');
    const [medRefills, setMedRefills] = useState('');
    const [visitDoctor, setVisitDoctor] = useState('');
    const [visitClinic, setVisitClinic] = useState('');
    const [visitReason, setVisitReason] = useState('');
    const [visitDate, setVisitDate] = useState('');
    const [visitNotes, setVisitNotes] = useState('');
    const [visitFollowUp, setVisitFollowUp] = useState('');
    const [goalType, setGoalType] = useState('');
    const [goalTarget, setGoalTarget] = useState('');
    const [goalProgress, setGoalProgress] = useState('');
    const [goalDeadline, setGoalDeadline] = useState('');
    const [emergencyName, setEmergencyName] = useState('');
    const [emergencyPhone, setEmergencyPhone] = useState('');
    const [emergencyType, setEmergencyType] = useState('');
    const [privacyLevel, setPrivacyLevel] = useState('shared-all'); // 'private', 'shared-guardians', 'shared-all'
    
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);
    const fileInputRef = useRef(null);

    const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
    const goalTypes = ['Steps', 'Running', 'Cycling', 'Yoga', 'Weight Loss', 'Weight Gain', 'Other'];
    const emergencyTypes = ['Hospital', 'Doctor', 'Ambulance', 'Poison Control', 'Family', 'Friend', 'Other'];

    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load health records when family changes
    useEffect(() => {
        const loadHealthData = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchHealthRecords(selectedFamily.id);
                const records = response.items || [];
                
                // Group records by member_id for backward compatibility
                const groupedData = {};
                records.forEach(record => {
                    if (!groupedData[record.member_id]) {
                        groupedData[record.member_id] = {
                            basicInfo: {},
                            records: [],
                            medications: [],
                            visits: [],
                            goals: [],
                            emergencyContacts: []
                        };
                    }
                    
                    // Categorize by record type
                    if (record.record_type === 'basic_info') {
                        groupedData[record.member_id].basicInfo = record.data || {};
                    } else if (record.record_type === 'medical_record') {
                        groupedData[record.member_id].records.push(record);
                    } else if (record.record_type === 'medication') {
                        groupedData[record.member_id].medications.push(record);
                    } else if (record.record_type === 'visit') {
                        groupedData[record.member_id].visits.push(record);
                    } else if (record.record_type === 'goal') {
                        groupedData[record.member_id].goals.push(record);
                    } else if (record.record_type === 'emergency_contact') {
                        groupedData[record.member_id].emergencyContacts.push(record);
                    }
                });
                
                setHealthData(groupedData);
            } catch (error) {
                console.error('Error loading health data:', error);
            }
        };
        loadHealthData();
    }, [selectedFamily]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showAddModal || showPremiumModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [showAddModal, showPremiumModal]);

    // Data now persists via API - no localStorage needed

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id.toString());
        setShowFamilyDropdown(false);
    };

    const getFamilyMembers = () => {
        if (!selectedFamily) return [];
        return selectedFamily.members?.filter(m => m && typeof m === 'object' && m.name) || [];
    };

    const getMemberHealthData = (memberName) => {
        return healthData[memberName] || {
            basicInfo: {},
            medicalRecords: [],
            medications: [],
            doctorVisits: [],
            fitnessGoals: [],
            emergencyContacts: []
        };
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                alert('File size must be less than 10MB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setRecordFile({
                    name: file.name,
                    type: file.type,
                    data: reader.result
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveBasicInfo = () => {
        if (!selectedMember) {
            alert('Please select a family member');
            return;
        }

        const updatedData = { ...healthData };
        if (!updatedData[selectedMember]) {
            updatedData[selectedMember] = {
                basicInfo: {},
                medicalRecords: [],
                medications: [],
                doctorVisits: [],
                fitnessGoals: [],
                emergencyContacts: []
            };
        }

        updatedData[selectedMember].basicInfo = {
            bloodGroup,
            height,
            weight,
            allergies,
            updatedAt: new Date().toISOString()
        };

        setHealthData(updatedData);
        resetForm();
    };

    const handleSaveRecord = () => {
        if (!selectedMember || !recordTitle) {
            alert('Please fill in required fields');
            return;
        }

        const updatedData = { ...healthData };
        if (!updatedData[selectedMember]) {
            updatedData[selectedMember] = {
                basicInfo: {},
                medicalRecords: [],
                medications: [],
                doctorVisits: [],
                fitnessGoals: [],
                emergencyContacts: []
            };
        }

        const newRecord = {
            id: editingItem?.id || Date.now(),
            title: recordTitle,
            file: recordFile,
            date: recordDate,
            doctor: recordDoctor,
            tags: recordTags.split(',').map(t => t.trim()).filter(t => t),
            privacyLevel,
            addedAt: editingItem?.addedAt || new Date().toISOString()
        };

        if (editingItem) {
            updatedData[selectedMember].medicalRecords = updatedData[selectedMember].medicalRecords.map(r =>
                r.id === editingItem.id ? newRecord : r
            );
        } else {
            updatedData[selectedMember].medicalRecords.push(newRecord);
        }

        setHealthData(updatedData);
        resetForm();
    };

    const handleSaveMedication = () => {
        if (!selectedMember || !medName) {
            alert('Please fill in required fields');
            return;
        }

        const updatedData = { ...healthData };
        if (!updatedData[selectedMember]) {
            updatedData[selectedMember] = {
                basicInfo: {},
                medicalRecords: [],
                medications: [],
                doctorVisits: [],
                fitnessGoals: [],
                emergencyContacts: []
            };
        }

        const newMedication = {
            id: editingItem?.id || Date.now(),
            name: medName,
            dosage: medDosage,
            schedule: medSchedule,
            startDate: medStartDate,
            endDate: medEndDate,
            refills: medRefills,
            privacyLevel,
            addedAt: editingItem?.addedAt || new Date().toISOString()
        };

        if (editingItem) {
            updatedData[selectedMember].medications = updatedData[selectedMember].medications.map(m =>
                m.id === editingItem.id ? newMedication : m
            );
        } else {
            updatedData[selectedMember].medications.push(newMedication);
        }

        setHealthData(updatedData);
        resetForm();
    };

    const handleSaveVisit = () => {
        if (!selectedMember || !visitDoctor || !visitDate) {
            alert('Please fill in required fields');
            return;
        }

        const updatedData = { ...healthData };
        if (!updatedData[selectedMember]) {
            updatedData[selectedMember] = {
                basicInfo: {},
                medicalRecords: [],
                medications: [],
                doctorVisits: [],
                fitnessGoals: [],
                emergencyContacts: []
            };
        }

        const newVisit = {
            id: editingItem?.id || Date.now(),
            doctor: visitDoctor,
            clinic: visitClinic,
            reason: visitReason,
            date: visitDate,
            notes: visitNotes,
            followUpDate: visitFollowUp,
            privacyLevel,
            addedAt: editingItem?.addedAt || new Date().toISOString()
        };

        if (editingItem) {
            updatedData[selectedMember].doctorVisits = updatedData[selectedMember].doctorVisits.map(v =>
                v.id === editingItem.id ? newVisit : v
            );
        } else {
            updatedData[selectedMember].doctorVisits.push(newVisit);
        }

        setHealthData(updatedData);
        resetForm();
    };

    const handleSaveGoal = () => {
        if (!selectedMember || !goalType || !goalTarget) {
            alert('Please fill in required fields');
            return;
        }

        const updatedData = { ...healthData };
        if (!updatedData[selectedMember]) {
            updatedData[selectedMember] = {
                basicInfo: {},
                medicalRecords: [],
                medications: [],
                doctorVisits: [],
                fitnessGoals: [],
                emergencyContacts: []
            };
        }

        const newGoal = {
            id: editingItem?.id || Date.now(),
            type: goalType,
            target: goalTarget,
            progress: goalProgress || '0',
            deadline: goalDeadline,
            privacyLevel,
            addedAt: editingItem?.addedAt || new Date().toISOString()
        };

        if (editingItem) {
            updatedData[selectedMember].fitnessGoals = updatedData[selectedMember].fitnessGoals.map(g =>
                g.id === editingItem.id ? newGoal : g
            );
        } else {
            updatedData[selectedMember].fitnessGoals.push(newGoal);
        }

        setHealthData(updatedData);
        resetForm();
    };

    const handleSaveEmergency = () => {
        if (!emergencyName || !emergencyPhone) {
            alert('Please fill in required fields');
            return;
        }

        const updatedData = { ...healthData };
        // Emergency contacts are family-wide, store under a special key
        if (!updatedData['_familyEmergency']) {
            updatedData['_familyEmergency'] = {
                emergencyContacts: []
            };
        }

        const newContact = {
            id: editingItem?.id || Date.now(),
            name: emergencyName,
            phone: emergencyPhone,
            type: emergencyType,
            addedAt: editingItem?.addedAt || new Date().toISOString()
        };

        if (editingItem) {
            updatedData['_familyEmergency'].emergencyContacts = updatedData['_familyEmergency'].emergencyContacts.map(c =>
                c.id === editingItem.id ? newContact : c
            );
        } else {
            updatedData['_familyEmergency'].emergencyContacts.push(newContact);
        }

        setHealthData(updatedData);
        resetForm();
    };

    const handleDeleteItem = (type, id) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;

        const updatedData = { ...healthData };
        
        if (type === 'emergency') {
            updatedData['_familyEmergency'].emergencyContacts = updatedData['_familyEmergency'].emergencyContacts.filter(c => c.id !== id);
        } else if (selectedMember) {
            const typeMap = {
                'record': 'medicalRecords',
                'medication': 'medications',
                'visit': 'doctorVisits',
                'goal': 'fitnessGoals'
            };
            updatedData[selectedMember][typeMap[type]] = updatedData[selectedMember][typeMap[type]].filter(item => item.id !== id);
        }

        setHealthData(updatedData);
        setOpenMenuId(null);
    };

    const handleEditItem = (type, item) => {
        setEditingItem(item);
        setModalType(type);
        
        if (type === 'basicInfo') {
            const info = getMemberHealthData(selectedMember).basicInfo;
            setBloodGroup(info.bloodGroup || '');
            setHeight(info.height || '');
            setWeight(info.weight || '');
            setAllergies(info.allergies || '');
        } else if (type === 'record') {
            setRecordTitle(item.title);
            setRecordFile(item.file);
            setRecordDate(item.date);
            setRecordDoctor(item.doctor);
            setRecordTags(item.tags?.join(', ') || '');
            setPrivacyLevel(item.privacyLevel || 'shared-all');
        } else if (type === 'medication') {
            setMedName(item.name);
            setMedDosage(item.dosage);
            setMedSchedule(item.schedule);
            setMedStartDate(item.startDate);
            setMedEndDate(item.endDate);
            setMedRefills(item.refills);
            setPrivacyLevel(item.privacyLevel || 'shared-all');
        } else if (type === 'visit') {
            setVisitDoctor(item.doctor);
            setVisitClinic(item.clinic);
            setVisitReason(item.reason);
            setVisitDate(item.date);
            setVisitNotes(item.notes);
            setVisitFollowUp(item.followUpDate);
            setPrivacyLevel(item.privacyLevel || 'shared-all');
        } else if (type === 'goal') {
            setGoalType(item.type);
            setGoalTarget(item.target);
            setGoalProgress(item.progress);
            setGoalDeadline(item.deadline);
            setPrivacyLevel(item.privacyLevel || 'shared-all');
        } else if (type === 'emergency') {
            setEmergencyName(item.name);
            setEmergencyPhone(item.phone);
            setEmergencyType(item.type);
        }
        
        setShowAddModal(true);
        setOpenMenuId(null);
    };

    const resetForm = () => {
        setBloodGroup('');
        setHeight('');
        setWeight('');
        setAllergies('');
        setRecordTitle('');
        setRecordFile(null);
        setRecordDate('');
        setRecordDoctor('');
        setRecordTags('');
        setMedName('');
        setMedDosage('');
        setMedSchedule('');
        setMedStartDate('');
        setMedEndDate('');
        setMedRefills('');
        setVisitDoctor('');
        setVisitClinic('');
        setVisitReason('');
        setVisitDate('');
        setVisitNotes('');
        setVisitFollowUp('');
        setGoalType('');
        setGoalTarget('');
        setGoalProgress('');
        setGoalDeadline('');
        setEmergencyName('');
        setEmergencyPhone('');
        setEmergencyType('');
        setPrivacyLevel('shared-all');
        setEditingItem(null);
        setShowAddModal(false);
        setModalType('');
        
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getPrivacyIcon = (level) => {
        switch (level) {
            case 'private':
                return <Shield className="w-4 h-4 text-red-500" title="Private" />;
            case 'shared-guardians':
                return <Shield className="w-4 h-4 text-yellow-500" title="Shared with Guardians" />;
            case 'shared-all':
                return <Eye className="w-4 h-4 text-green-500" title="Shared with All" />;
            default:
                return null;
        }
    };

    const getEmergencyContacts = () => {
        return healthData['_familyEmergency']?.emergencyContacts || [];
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily?.photo && (
                                        <img 
                                            src={selectedFamily.photo} 
                                            alt={selectedFamily.name}
                                            className="w-6 h-6 rounded-full object-cover"
                                        />
                                    )}
                                    <span className="text-white font-semibold">
                                        {selectedFamily ? selectedFamily.name : 'Mi Famlis'}
                                    </span>
                                    <ChevronDown className="w-4 h-4 text-white" />
                                </button>

                                {showFamilyDropdown && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                                        {families.map((family) => (
                                            <button
                                                key={family.id}
                                                onClick={() => handleFamilySelect(family)}
                                                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3"
                                            >
                                                {family.photo && (
                                                    <img 
                                                        src={family.photo} 
                                                        alt={family.name}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                )}
                                                <div>
                                                    <div className="font-semibold text-gray-900">{family.name}</div>
                                                    {family.tagline && (
                                                        <div className="text-sm text-gray-500">{family.tagline}</div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                        <div className="border-t border-gray-200 my-2"></div>
                                        <button
                                            onClick={() => navigate('/famlis')}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-primary-blue font-semibold"
                                        >
                                            <Plus className="w-5 h-5" />
                                            <span>Add Family</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Premium Button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowPremiumModal(true)}
                                    className="relative px-4 py-2 rounded-lg font-semibold text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-2 border-white/50 overflow-hidden group"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.6)'
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-2">
                                        <span className="hidden sm:inline">Go Premium</span>
                                        <span className="sm:hidden">Premium</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                </button>
                                
                                {/* Golden Crown Overlay */}
                                <div className="absolute -top-1 -right-1 transform rotate-12">
                                    <Crown className="w-5 h-5 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                                </div>
                            </div>
                            
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                >
                                    <UserAvatar size="medium" />
                                </button>

                                {/* User Dropdown Menu */}
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-200">
                                            <p className="text-sm font-semibold text-gray-800">User Account</p>
                                            <p className="text-xs text-gray-500">Manage your profile</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Profile settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <User className="w-5 h-5 text-gray-600" />
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Settings className="w-5 h-5 text-gray-600" />
                                            <span>Settings</span>
                                        </button>
                                        <div className="border-t border-gray-200 my-2"></div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                handleLogout();
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Health & Wellness</span>
                </div>

                {/* Header */}
                <div className="mb-6 flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <Heart className="w-8 h-8 text-rose-500" />
                            Health & Wellness
                        </h2>
                        <p className="text-gray-600 mt-1">Centralize family health and wellness data</p>
                    </div>
                    <button
                        onClick={() => setShowFirstAidModal(true)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white font-semibold transition-all duration-200 hover:bg-red-600 hover:shadow-lg"
                    >
                        <AlertCircle className="w-5 h-5" />
                        <span>First Aid</span>
                    </button>
                </div>

                {/* Member Selection */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Family Member</label>
                    <select
                        value={selectedMember || ''}
                        onChange={(e) => setSelectedMember(e.target.value)}
                        className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    >
                        <option value="">Choose a member...</option>
                        {getFamilyMembers().map((member, index) => (
                            <option key={index} value={member}>{member}</option>
                        ))}
                    </select>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                    <div className="flex gap-4 overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${
                                activeTab === 'overview'
                                    ? 'text-rose-500 border-b-2 border-rose-500'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <Activity className="w-4 h-4 inline mr-2" />
                            Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('records')}
                            className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${
                                activeTab === 'records'
                                    ? 'text-rose-500 border-b-2 border-rose-500'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                            disabled={!selectedMember}
                        >
                            <FileText className="w-4 h-4 inline mr-2" />
                            Medical Records
                        </button>
                        <button
                            onClick={() => setActiveTab('medications')}
                            className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${
                                activeTab === 'medications'
                                    ? 'text-rose-500 border-b-2 border-rose-500'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                            disabled={!selectedMember}
                        >
                            <Pill className="w-4 h-4 inline mr-2" />
                            Medications
                        </button>
                        <button
                            onClick={() => setActiveTab('visits')}
                            className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${
                                activeTab === 'visits'
                                    ? 'text-rose-500 border-b-2 border-rose-500'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                            disabled={!selectedMember}
                        >
                            <Calendar className="w-4 h-4 inline mr-2" />
                            Doctor Visits
                        </button>
                        <button
                            onClick={() => setActiveTab('fitness')}
                            className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${
                                activeTab === 'fitness'
                                    ? 'text-rose-500 border-b-2 border-rose-500'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                            disabled={!selectedMember}
                        >
                            <Target className="w-4 h-4 inline mr-2" />
                            Fitness Goals
                        </button>
                        <button
                            onClick={() => setActiveTab('emergency')}
                            className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${
                                activeTab === 'emergency'
                                    ? 'text-rose-500 border-b-2 border-rose-500'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <AlertCircle className="w-4 h-4 inline mr-2" />
                            Emergency
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        {!selectedMember ? (
                            <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">Please select a family member to view their health overview</p>
                            </div>
                        ) : (
                            <>
                                {/* Basic Info Card */}
                                <div className="bg-white rounded-xl shadow-md p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-xl font-bold text-gray-900">Basic Health Information</h3>
                                        <button
                                            onClick={() => {
                                                const info = getMemberHealthData(selectedMember).basicInfo;
                                                setBloodGroup(info.bloodGroup || '');
                                                setHeight(info.height || '');
                                                setWeight(info.weight || '');
                                                setAllergies(info.allergies || '');
                                                setModalType('basicInfo');
                                                setShowAddModal(true);
                                            }}
                                            className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
                                        >
                                            <Edit2 className="w-4 h-4 inline mr-2" />
                                            Edit
                                        </button>
                                    </div>
                                    {(() => {
                                        const info = getMemberHealthData(selectedMember).basicInfo;
                                        return Object.keys(info).length === 0 ? (
                                            <p className="text-gray-500">No basic information added yet</p>
                                        ) : (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                {info.bloodGroup && (
                                                    <div className="p-4 bg-red-50 rounded-lg">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Droplet className="w-4 h-4 text-red-500" />
                                                            <span className="text-sm text-gray-600">Blood Group</span>
                                                        </div>
                                                        <p className="text-lg font-bold text-gray-900">{info.bloodGroup}</p>
                                                    </div>
                                                )}
                                                {info.height && (
                                                    <div className="p-4 bg-blue-50 rounded-lg">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Ruler className="w-4 h-4 text-blue-500" />
                                                            <span className="text-sm text-gray-600">Height</span>
                                                        </div>
                                                        <p className="text-lg font-bold text-gray-900">{info.height} cm</p>
                                                    </div>
                                                )}
                                                {info.weight && (
                                                    <div className="p-4 bg-green-50 rounded-lg">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Scale className="w-4 h-4 text-green-500" />
                                                            <span className="text-sm text-gray-600">Weight</span>
                                                        </div>
                                                        <p className="text-lg font-bold text-gray-900">{info.weight} kg</p>
                                                    </div>
                                                )}
                                                {info.allergies && (
                                                    <div className="p-4 bg-yellow-50 rounded-lg col-span-2 md:col-span-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <AlertCircle className="w-4 h-4 text-yellow-500" />
                                                            <span className="text-sm text-gray-600">Allergies</span>
                                                        </div>
                                                        <p className="text-sm font-medium text-gray-900">{info.allergies}</p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Quick Stats */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-white rounded-xl shadow-md p-6">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <FileText className="w-5 h-5 text-blue-600" />
                                            </div>
                                            <span className="text-sm text-gray-600">Medical Records</span>
                                        </div>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {getMemberHealthData(selectedMember).medicalRecords.length}
                                        </p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-md p-6">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="p-2 bg-purple-100 rounded-lg">
                                                <Pill className="w-5 h-5 text-purple-600" />
                                            </div>
                                            <span className="text-sm text-gray-600">Medications</span>
                                        </div>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {getMemberHealthData(selectedMember).medications.length}
                                        </p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-md p-6">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="p-2 bg-green-100 rounded-lg">
                                                <Calendar className="w-5 h-5 text-green-600" />
                                            </div>
                                            <span className="text-sm text-gray-600">Doctor Visits</span>
                                        </div>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {getMemberHealthData(selectedMember).doctorVisits.length}
                                        </p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-md p-6">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="p-2 bg-orange-100 rounded-lg">
                                                <Target className="w-5 h-5 text-orange-600" />
                                            </div>
                                            <span className="text-sm text-gray-600">Fitness Goals</span>
                                        </div>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {getMemberHealthData(selectedMember).fitnessGoals.length}
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Medical Records Tab - Content to be continued in next part */}
                {activeTab === 'records' && selectedMember && (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search records..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent w-64"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setModalType('record');
                                    setShowAddModal(true);
                                }}
                                className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors flex items-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Add Record
                            </button>
                        </div>

                        {getMemberHealthData(selectedMember).medicalRecords.length === 0 ? (
                            <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No medical records yet</p>
                                <button
                                    onClick={() => {
                                        setModalType('record');
                                        setShowAddModal(true);
                                    }}
                                    className="px-6 py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
                                >
                                    Add First Record
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {getMemberHealthData(selectedMember).medicalRecords
                                    .filter(record => 
                                        !searchQuery || 
                                        record.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                        record.doctor?.toLowerCase().includes(searchQuery.toLowerCase())
                                    )
                                    .map((record) => (
                                    <div key={record.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h4 className="text-lg font-bold text-gray-900 mb-1">{record.title}</h4>
                                                {record.date && (
                                                    <p className="text-sm text-gray-600">
                                                        {new Date(record.date).toLocaleDateString()}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {getPrivacyIcon(record.privacyLevel)}
                                                <div className="relative">
                                                    <button
                                                        onClick={() => setOpenMenuId(openMenuId === record.id ? null : record.id)}
                                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                    >
                                                        <MoreVertical className="w-5 h-5 text-gray-600" />
                                                    </button>
                                                    {openMenuId === record.id && (
                                                        <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                            <button
                                                                onClick={() => handleEditItem('record', record)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                                <span>Edit</span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteItem('record', record.id)}
                                                                className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                <span>Delete</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {record.doctor && (
                                            <p className="text-sm text-gray-600 mb-2">
                                                <strong>Doctor:</strong> {record.doctor}
                                            </p>
                                        )}
                                        {record.file && (
                                            <div className="mt-3 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                                                <span className="text-sm text-gray-700 truncate">{record.file.name}</span>
                                                <a
                                                    href={record.file.data}
                                                    download={record.file.name}
                                                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                                >
                                                    <Download className="w-4 h-4 text-gray-600" />
                                                </a>
                                            </div>
                                        )}
                                        {record.tags && record.tags.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {record.tags.map((tag, idx) => (
                                                    <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Medications Tab */}
                {activeTab === 'medications' && selectedMember && (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Medications & Allergies</h3>
                            <button
                                onClick={() => {
                                    setModalType('medication');
                                    setShowAddModal(true);
                                }}
                                className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors flex items-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Add Medication
                            </button>
                        </div>

                        {getMemberHealthData(selectedMember).medications.length === 0 ? (
                            <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                <Pill className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No medications added yet</p>
                                <button
                                    onClick={() => {
                                        setModalType('medication');
                                        setShowAddModal(true);
                                    }}
                                    className="px-6 py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
                                >
                                    Add First Medication
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {getMemberHealthData(selectedMember).medications.map((med) => (
                                    <div key={med.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h4 className="text-lg font-bold text-gray-900 mb-1">{med.name}</h4>
                                                {med.dosage && (
                                                    <p className="text-sm text-gray-600">{med.dosage}</p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {getPrivacyIcon(med.privacyLevel)}
                                                <div className="relative">
                                                    <button
                                                        onClick={() => setOpenMenuId(openMenuId === med.id ? null : med.id)}
                                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                    >
                                                        <MoreVertical className="w-5 h-5 text-gray-600" />
                                                    </button>
                                                    {openMenuId === med.id && (
                                                        <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                            <button
                                                                onClick={() => handleEditItem('medication', med)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                                <span>Edit</span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteItem('medication', med.id)}
                                                                className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                <span>Delete</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {med.schedule && (
                                            <p className="text-sm text-gray-600 mb-2">
                                                <strong>Schedule:</strong> {med.schedule}
                                            </p>
                                        )}
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            {med.startDate && (
                                                <div className="text-gray-600">
                                                    <strong>Start:</strong> {new Date(med.startDate).toLocaleDateString()}
                                                </div>
                                            )}
                                            {med.endDate && (
                                                <div className="text-gray-600">
                                                    <strong>End:</strong> {new Date(med.endDate).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>
                                        {med.refills && (
                                            <div className="mt-3 p-2 bg-yellow-50 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <Bell className="w-4 h-4 text-yellow-600" />
                                                    <span className="text-sm text-yellow-800">Refills: {med.refills}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Doctor Visits Tab */}
                {activeTab === 'visits' && selectedMember && (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Doctor Visits</h3>
                            <button
                                onClick={() => {
                                    setModalType('visit');
                                    setShowAddModal(true);
                                }}
                                className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors flex items-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Add Visit
                            </button>
                        </div>

                        {getMemberHealthData(selectedMember).doctorVisits.length === 0 ? (
                            <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No doctor visits logged yet</p>
                                <button
                                    onClick={() => {
                                        setModalType('visit');
                                        setShowAddModal(true);
                                    }}
                                    className="px-6 py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
                                >
                                    Log First Visit
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {getMemberHealthData(selectedMember).doctorVisits
                                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                                    .map((visit) => (
                                    <div key={visit.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h4 className="text-lg font-bold text-gray-900">{visit.doctor}</h4>
                                                    <span className="text-sm text-gray-500">
                                                        {new Date(visit.date).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {visit.clinic && (
                                                    <p className="text-sm text-gray-600 mb-1">{visit.clinic}</p>
                                                )}
                                                {visit.reason && (
                                                    <p className="text-sm text-gray-700 mb-2">
                                                        <strong>Reason:</strong> {visit.reason}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {getPrivacyIcon(visit.privacyLevel)}
                                                <div className="relative">
                                                    <button
                                                        onClick={() => setOpenMenuId(openMenuId === visit.id ? null : visit.id)}
                                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                    >
                                                        <MoreVertical className="w-5 h-5 text-gray-600" />
                                                    </button>
                                                    {openMenuId === visit.id && (
                                                        <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                            <button
                                                                onClick={() => handleEditItem('visit', visit)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                                <span>Edit</span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteItem('visit', visit.id)}
                                                                className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                <span>Delete</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {visit.notes && (
                                            <div className="p-3 bg-gray-50 rounded-lg mb-3">
                                                <p className="text-sm text-gray-700">{visit.notes}</p>
                                            </div>
                                        )}
                                        {visit.followUpDate && (
                                            <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                                                <Bell className="w-4 h-4 text-blue-600" />
                                                <span className="text-sm text-blue-800">
                                                    Follow-up: {new Date(visit.followUpDate).toLocaleDateString()}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Fitness Goals Tab */}
                {activeTab === 'fitness' && selectedMember && (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Fitness Goals & Challenges</h3>
                            <button
                                onClick={() => {
                                    setModalType('goal');
                                    setShowAddModal(true);
                                }}
                                className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors flex items-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Add Goal
                            </button>
                        </div>

                        {/* Coming Soon Notice */}
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-md p-6 mb-6">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-white rounded-lg">
                                    <Apple className="w-6 h-6 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Wearable Integration Coming Soon!</h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                        Soon you'll be able to connect Apple Health, Fitbit, and Google Fit to automatically track your progress.
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <TrendingUp className="w-4 h-4" />
                                        <span>Leaderboards and family challenges coming too!</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {getMemberHealthData(selectedMember).fitnessGoals.length === 0 ? (
                            <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No fitness goals set yet</p>
                                <button
                                    onClick={() => {
                                        setModalType('goal');
                                        setShowAddModal(true);
                                    }}
                                    className="px-6 py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
                                >
                                    Set First Goal
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {getMemberHealthData(selectedMember).fitnessGoals.map((goal) => {
                                    const progressPercent = goal.target > 0 ? Math.min((parseInt(goal.progress) / parseInt(goal.target)) * 100, 100) : 0;
                                    return (
                                        <div key={goal.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex-1">
                                                    <h4 className="text-lg font-bold text-gray-900 mb-1">{goal.type}</h4>
                                                    <p className="text-sm text-gray-600">Target: {goal.target}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {getPrivacyIcon(goal.privacyLevel)}
                                                    <div className="relative">
                                                        <button
                                                            onClick={() => setOpenMenuId(openMenuId === goal.id ? null : goal.id)}
                                                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                        >
                                                            <MoreVertical className="w-5 h-5 text-gray-600" />
                                                        </button>
                                                        {openMenuId === goal.id && (
                                                            <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                                <button
                                                                    onClick={() => handleEditItem('goal', goal)}
                                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                    <span>Edit</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteItem('goal', goal.id)}
                                                                    className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                    <span>Delete</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Progress Bar */}
                                            <div className="mb-3">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm text-gray-600">Progress</span>
                                                    <span className="text-sm font-bold text-gray-900">{progressPercent.toFixed(0)}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-3">
                                                    <div
                                                        className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full transition-all duration-300"
                                                        style={{ width: `${progressPercent}%` }}
                                                    ></div>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {goal.progress} / {goal.target}
                                                </p>
                                            </div>
                                            
                                            {goal.deadline && (
                                                <div className="text-sm text-gray-600">
                                                    <strong>Deadline:</strong> {new Date(goal.deadline).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Emergency Tab */}
                {activeTab === 'emergency' && (
                    <div>
                        <div className="mb-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Emergency Contacts & First Aid</h3>
                            <p className="text-gray-600">Quick access to emergency information for your family</p>
                        </div>

                        {/* Emergency Button */}
                        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl shadow-lg p-6 mb-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-2xl font-bold mb-2">Emergency Mode</h4>
                                    <p className="text-red-100">Quick access to all emergency contacts</p>
                                </div>
                                <button className="p-4 bg-white text-red-600 rounded-full hover:bg-red-50 transition-colors">
                                    <Phone className="w-8 h-8" />
                                </button>
                            </div>
                        </div>

                        {/* Emergency Contacts */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-bold text-gray-900">Emergency Contacts</h4>
                                <button
                                    onClick={() => {
                                        setModalType('emergency');
                                        setShowAddModal(true);
                                    }}
                                    className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors flex items-center gap-2"
                                >
                                    <Plus className="w-5 h-5" />
                                    Add Contact
                                </button>
                            </div>

                            {getEmergencyContacts().length === 0 ? (
                                <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                    <Phone className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500 mb-4">No emergency contacts added yet</p>
                                    <button
                                        onClick={() => {
                                            setModalType('emergency');
                                            setShowAddModal(true);
                                        }}
                                        className="px-6 py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
                                    >
                                        Add First Contact
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {getEmergencyContacts().map((contact) => (
                                        <div key={contact.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex items-start gap-3 flex-1">
                                                    <div className="p-2 bg-red-100 rounded-lg">
                                                        <Phone className="w-5 h-5 text-red-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h5 className="font-bold text-gray-900 mb-1">{contact.name}</h5>
                                                        <p className="text-sm text-gray-600 mb-1">{contact.phone}</p>
                                                        {contact.type && (
                                                            <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                                                {contact.type}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="relative">
                                                    <button
                                                        onClick={() => setOpenMenuId(openMenuId === contact.id ? null : contact.id)}
                                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                    >
                                                        <MoreVertical className="w-5 h-5 text-gray-600" />
                                                    </button>
                                                    {openMenuId === contact.id && (
                                                        <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                            <button
                                                                onClick={() => handleEditItem('emergency', contact)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                                <span>Edit</span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteItem('emergency', contact.id)}
                                                                className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                <span>Delete</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <a
                                                href={`tel:${contact.phone}`}
                                                className="block w-full px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-center font-medium"
                                            >
                                                Call Now
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-2xl w-full my-8">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingItem ? 'Edit' : 'Add'} {
                                    modalType === 'basicInfo' ? 'Basic Information' :
                                    modalType === 'record' ? 'Medical Record' :
                                    modalType === 'medication' ? 'Medication' :
                                    modalType === 'visit' ? 'Doctor Visit' :
                                    modalType === 'goal' ? 'Fitness Goal' :
                                    modalType === 'emergency' ? 'Emergency Contact' : ''
                                }
                            </h2>
                            <button
                                onClick={resetForm}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
                            {/* Basic Info Form */}
                            {modalType === 'basicInfo' && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Blood Group</label>
                                            <select
                                                value={bloodGroup}
                                                onChange={(e) => setBloodGroup(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            >
                                                <option value="">Select...</option>
                                                {bloodGroups.map(bg => (
                                                    <option key={bg} value={bg}>{bg}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
                                            <input
                                                type="number"
                                                value={height}
                                                onChange={(e) => setHeight(e.target.value)}
                                                placeholder="170"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Weight (kg)</label>
                                            <input
                                                type="number"
                                                value={weight}
                                                onChange={(e) => setWeight(e.target.value)}
                                                placeholder="70"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Allergies</label>
                                        <textarea
                                            value={allergies}
                                            onChange={(e) => setAllergies(e.target.value)}
                                            placeholder="List any allergies (food, medicine, environmental)..."
                                            rows="3"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                                        />
                                    </div>
                                </>
                            )}

                            {/* Medical Record Form */}
                            {modalType === 'record' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                        <input
                                            type="text"
                                            value={recordTitle}
                                            onChange={(e) => setRecordTitle(e.target.value)}
                                            placeholder="e.g., Blood Test Results"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                                            <input
                                                type="date"
                                                value={recordDate}
                                                onChange={(e) => setRecordDate(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Doctor</label>
                                            <input
                                                type="text"
                                                value={recordDoctor}
                                                onChange={(e) => setRecordDoctor(e.target.value)}
                                                placeholder="Dr. Smith"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Tags (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={recordTags}
                                            onChange={(e) => setRecordTags(e.target.value)}
                                            placeholder="diagnosis, lab, prescription"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Upload File (Max 10MB)</label>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                            onChange={handleFileSelect}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        />
                                        {recordFile && (
                                            <p className="text-sm text-gray-600 mt-2">Selected: {recordFile.name}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Level</label>
                                        <select
                                            value={privacyLevel}
                                            onChange={(e) => setPrivacyLevel(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        >
                                            <option value="shared-all">Shared with All Members</option>
                                            <option value="shared-guardians">Shared with Guardians</option>
                                            <option value="private">Private</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* Medication Form */}
                            {modalType === 'medication' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Medication Name *</label>
                                        <input
                                            type="text"
                                            value={medName}
                                            onChange={(e) => setMedName(e.target.value)}
                                            placeholder="e.g., Aspirin"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Dosage</label>
                                            <input
                                                type="text"
                                                value={medDosage}
                                                onChange={(e) => setMedDosage(e.target.value)}
                                                placeholder="e.g., 100mg"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Schedule</label>
                                            <input
                                                type="text"
                                                value={medSchedule}
                                                onChange={(e) => setMedSchedule(e.target.value)}
                                                placeholder="e.g., Twice daily"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                            <input
                                                type="date"
                                                value={medStartDate}
                                                onChange={(e) => setMedStartDate(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                            <input
                                                type="date"
                                                value={medEndDate}
                                                onChange={(e) => setMedEndDate(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Refills Remaining</label>
                                        <input
                                            type="number"
                                            value={medRefills}
                                            onChange={(e) => setMedRefills(e.target.value)}
                                            placeholder="e.g., 2"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Level</label>
                                        <select
                                            value={privacyLevel}
                                            onChange={(e) => setPrivacyLevel(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        >
                                            <option value="shared-all">Shared with All Members</option>
                                            <option value="shared-guardians">Shared with Guardians</option>
                                            <option value="private">Private</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* Doctor Visit Form */}
                            {modalType === 'visit' && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Doctor Name *</label>
                                            <input
                                                type="text"
                                                value={visitDoctor}
                                                onChange={(e) => setVisitDoctor(e.target.value)}
                                                placeholder="Dr. Smith"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Visit Date *</label>
                                            <input
                                                type="date"
                                                value={visitDate}
                                                onChange={(e) => setVisitDate(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Clinic/Hospital</label>
                                        <input
                                            type="text"
                                            value={visitClinic}
                                            onChange={(e) => setVisitClinic(e.target.value)}
                                            placeholder="City Medical Center"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Visit</label>
                                        <input
                                            type="text"
                                            value={visitReason}
                                            onChange={(e) => setVisitReason(e.target.value)}
                                            placeholder="Regular checkup"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                        <textarea
                                            value={visitNotes}
                                            onChange={(e) => setVisitNotes(e.target.value)}
                                            placeholder="Prescription details, diagnosis, etc."
                                            rows="3"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Follow-up Date</label>
                                        <input
                                            type="date"
                                            value={visitFollowUp}
                                            onChange={(e) => setVisitFollowUp(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Level</label>
                                        <select
                                            value={privacyLevel}
                                            onChange={(e) => setPrivacyLevel(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        >
                                            <option value="shared-all">Shared with All Members</option>
                                            <option value="shared-guardians">Shared with Guardians</option>
                                            <option value="private">Private</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* Fitness Goal Form */}
                            {modalType === 'goal' && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Goal Type *</label>
                                            <select
                                                value={goalType}
                                                onChange={(e) => setGoalType(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            >
                                                <option value="">Select...</option>
                                                {goalTypes.map(type => (
                                                    <option key={type} value={type}>{type}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Target *</label>
                                            <input
                                                type="text"
                                                value={goalTarget}
                                                onChange={(e) => setGoalTarget(e.target.value)}
                                                placeholder="e.g., 10000 steps, 5 km"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Current Progress</label>
                                            <input
                                                type="number"
                                                value={goalProgress}
                                                onChange={(e) => setGoalProgress(e.target.value)}
                                                placeholder="0"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Deadline</label>
                                            <input
                                                type="date"
                                                value={goalDeadline}
                                                onChange={(e) => setGoalDeadline(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Level</label>
                                        <select
                                            value={privacyLevel}
                                            onChange={(e) => setPrivacyLevel(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        >
                                            <option value="shared-all">Shared with All Members</option>
                                            <option value="shared-guardians">Shared with Guardians</option>
                                            <option value="private">Private</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* Emergency Contact Form */}
                            {modalType === 'emergency' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name *</label>
                                        <input
                                            type="text"
                                            value={emergencyName}
                                            onChange={(e) => setEmergencyName(e.target.value)}
                                            placeholder="e.g., Dr. Smith, City Hospital"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                                            <input
                                                type="tel"
                                                value={emergencyPhone}
                                                onChange={(e) => setEmergencyPhone(e.target.value)}
                                                placeholder="+1 234 567 8900"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                                            <select
                                                value={emergencyType}
                                                onChange={(e) => setEmergencyType(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                                            >
                                                <option value="">Select...</option>
                                                {emergencyTypes.map(type => (
                                                    <option key={type} value={type}>{type}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
                            <button
                                onClick={resetForm}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    if (modalType === 'basicInfo') handleSaveBasicInfo();
                                    else if (modalType === 'record') handleSaveRecord();
                                    else if (modalType === 'medication') handleSaveMedication();
                                    else if (modalType === 'visit') handleSaveVisit();
                                    else if (modalType === 'goal') handleSaveGoal();
                                    else if (modalType === 'emergency') handleSaveEmergency();
                                }}
                                className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
                            >
                                {editingItem ? 'Update' : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* First Aid Modal */}
            {showFirstAidModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-4xl w-full my-8">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-100 rounded-lg">
                                    <AlertCircle className="w-6 h-6 text-red-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">First Aid Guide</h2>
                            </div>
                            <button
                                onClick={() => setShowFirstAidModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                            {/* Coming Soon Notice */}
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-white rounded-lg">
                                        <Activity className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-lg font-bold text-gray-900 mb-2">Detailed First Aid Videos Coming Soon!</h4>
                                        <p className="text-sm text-gray-600">
                                            We're working on adding step-by-step video tutorials for each first aid scenario. Stay tuned!
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* First Aid Tips Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Burns */}
                                <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-orange-500 text-white rounded-lg">
                                            <AlertCircle className="w-6 h-6" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">Burns</h3>
                                    </div>
                                    <div className="space-y-3 text-gray-700">
                                        <div>
                                            <p className="font-semibold text-orange-800 mb-1">Immediate Action:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Remove from heat source immediately</li>
                                                <li>Cool the burn under running water for 10-20 minutes</li>
                                                <li>Remove jewelry or tight clothing before swelling starts</li>
                                                <li>Do NOT use ice, butter, or ointments</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-orange-800 mb-1">Treatment:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Cover with sterile, non-stick bandage</li>
                                                <li>Take over-the-counter pain reliever if needed</li>
                                                <li>Keep burn elevated if possible</li>
                                            </ul>
                                        </div>
                                        <div className="pt-2 border-t border-orange-300">
                                            <p className="font-semibold text-red-700 text-sm">⚠️ Seek Medical Help If:</p>
                                            <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
                                                <li>Burn is larger than 3 inches</li>
                                                <li>Burn is on face, hands, feet, or genitals</li>
                                                <li>Appears deep or charred</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Cuts and Wounds */}
                                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-blue-500 text-white rounded-lg">
                                            <AlertCircle className="w-6 h-6" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">Cuts & Wounds</h3>
                                    </div>
                                    <div className="space-y-3 text-gray-700">
                                        <div>
                                            <p className="font-semibold text-blue-800 mb-1">Immediate Action:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Wash hands before treating</li>
                                                <li>Apply direct pressure with clean cloth for 5-10 minutes</li>
                                                <li>Elevate the wounded area above heart level</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-blue-800 mb-1">Treatment:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Clean wound gently with water</li>
                                                <li>Apply antibiotic ointment</li>
                                                <li>Cover with sterile bandage</li>
                                                <li>Change bandage daily</li>
                                            </ul>
                                        </div>
                                        <div className="pt-2 border-t border-blue-300">
                                            <p className="font-semibold text-red-700 text-sm">⚠️ Seek Medical Help If:</p>
                                            <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
                                                <li>Bleeding doesn't stop after 10 minutes</li>
                                                <li>Cut is deep or has jagged edges</li>
                                                <li>Signs of infection (redness, swelling, pus)</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Allergic Reaction */}
                                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-yellow-500 text-white rounded-lg">
                                            <AlertCircle className="w-6 h-6" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">Allergic Reaction</h3>
                                    </div>
                                    <div className="space-y-3 text-gray-700">
                                        <div>
                                            <p className="font-semibold text-yellow-800 mb-1">Mild Reaction:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Administer antihistamine (e.g., Benadryl)</li>
                                                <li>Apply cool compress to itchy areas</li>
                                                <li>Monitor for worsening symptoms</li>
                                                <li>Keep person calm and comfortable</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-yellow-800 mb-1">Severe Reaction (Anaphylaxis):</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Use EpiPen/auto-injector immediately if available</li>
                                                <li>Call emergency services (911/ambulance)</li>
                                                <li>Lay person flat (unless vomiting)</li>
                                                <li>Monitor breathing and pulse</li>
                                            </ul>
                                        </div>
                                        <div className="pt-2 border-t border-yellow-300">
                                            <p className="font-semibold text-red-700 text-sm">⚠️ Call Emergency If:</p>
                                            <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
                                                <li>Difficulty breathing or swallowing</li>
                                                <li>Swelling of face, lips, or throat</li>
                                                <li>Rapid pulse or dizziness</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Choking */}
                                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-red-500 text-white rounded-lg">
                                            <AlertCircle className="w-6 h-6" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">Choking</h3>
                                    </div>
                                    <div className="space-y-3 text-gray-700">
                                        <div>
                                            <p className="font-semibold text-red-800 mb-1">For Conscious Adult/Child:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Encourage coughing if they can breathe</li>
                                                <li>If cannot cough: Give 5 back blows between shoulder blades</li>
                                                <li>Then perform 5 abdominal thrusts (Heimlich maneuver)</li>
                                                <li>Repeat until object dislodges</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-red-800 mb-1">Heimlich Maneuver:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Stand behind person, wrap arms around waist</li>
                                                <li>Make fist above navel, below ribcage</li>
                                                <li>Grasp fist with other hand</li>
                                                <li>Quick, upward thrusts into abdomen</li>
                                            </ul>
                                        </div>
                                        <div className="pt-2 border-t border-red-300">
                                            <p className="font-semibold text-red-700 text-sm">⚠️ Call Emergency If:</p>
                                            <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
                                                <li>Object doesn't dislodge after several attempts</li>
                                                <li>Person becomes unconscious</li>
                                                <li>Skin turns blue</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* CPR */}
                                <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-purple-500 text-white rounded-lg">
                                            <Heart className="w-6 h-6" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">CPR (Adults)</h3>
                                    </div>
                                    <div className="space-y-3 text-gray-700">
                                        <div>
                                            <p className="font-semibold text-purple-800 mb-1">Before Starting:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Check if person is responsive (tap and shout)</li>
                                                <li>Call 911/emergency services immediately</li>
                                                <li>Check for breathing (no more than 10 seconds)</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-purple-800 mb-1">CPR Steps:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Place heel of hand on center of chest</li>
                                                <li>Place other hand on top, interlock fingers</li>
                                                <li>30 compressions: Push hard & fast (2 inches deep, 100-120/min)</li>
                                                <li>2 rescue breaths (tilt head, pinch nose, breathe into mouth)</li>
                                                <li>Continue 30:2 ratio until help arrives</li>
                                            </ul>
                                        </div>
                                        <div className="pt-2 border-t border-purple-300">
                                            <p className="font-semibold text-purple-700 text-sm">💡 Important:</p>
                                            <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
                                                <li>If untrained, do hands-only CPR (skip breaths)</li>
                                                <li>Don't stop until help arrives or person recovers</li>
                                                <li>Consider taking a CPR certification course</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Fractures/Sprains */}
                                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-green-500 text-white rounded-lg">
                                            <AlertCircle className="w-6 h-6" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">Fractures & Sprains</h3>
                                    </div>
                                    <div className="space-y-3 text-gray-700">
                                        <div>
                                            <p className="font-semibold text-green-800 mb-1">Immediate Action:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li>Immobilize the injured area</li>
                                                <li>Apply ice pack (wrapped in cloth) for 15-20 minutes</li>
                                                <li>Elevate above heart level if possible</li>
                                                <li>Don't try to realign the bone</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-green-800 mb-1">R.I.C.E. Method:</p>
                                            <ul className="list-disc list-inside space-y-1 text-sm">
                                                <li><strong>Rest:</strong> Avoid using injured area</li>
                                                <li><strong>Ice:</strong> Apply every 2-3 hours</li>
                                                <li><strong>Compression:</strong> Wrap with elastic bandage (not too tight)</li>
                                                <li><strong>Elevation:</strong> Keep above heart level</li>
                                            </ul>
                                        </div>
                                        <div className="pt-2 border-t border-green-300">
                                            <p className="font-semibold text-red-700 text-sm">⚠️ Seek Medical Help If:</p>
                                            <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
                                                <li>Severe pain or deformity visible</li>
                                                <li>Numbness or tingling</li>
                                                <li>Cannot bear weight on injured limb</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Emergency Numbers Section */}
                            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border-2 border-red-300">
                                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Phone className="w-6 h-6 text-red-600" />
                                    Emergency Contacts
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="text-center p-3 bg-white rounded-lg">
                                        <p className="text-sm text-gray-600 mb-1">Emergency</p>
                                        <p className="text-2xl font-bold text-red-600">911</p>
                                    </div>
                                    <div className="text-center p-3 bg-white rounded-lg">
                                        <p className="text-sm text-gray-600 mb-1">Poison Control</p>
                                        <p className="text-lg font-bold text-purple-600">1-800-222-1222</p>
                                    </div>
                                    <div className="text-center p-3 bg-white rounded-lg">
                                        <p className="text-sm text-gray-600 mb-1">Suicide Hotline</p>
                                        <p className="text-lg font-bold text-blue-600">988</p>
                                    </div>
                                    <div className="text-center p-3 bg-white rounded-lg">
                                        <p className="text-sm text-gray-600 mb-1">Non-Emergency</p>
                                        <p className="text-lg font-bold text-green-600">311</p>
                                    </div>
                                </div>
                            </div>

                            {/* Disclaimer */}
                            <div className="bg-gray-100 rounded-lg p-4 text-center text-sm text-gray-600">
                                <p>⚠️ This guide is for informational purposes only. In case of serious emergency, always call 911 or seek professional medical help immediately. Consider taking certified first aid and CPR courses for proper training.</p>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end rounded-b-2xl">
                            <button
                                onClick={() => setShowFirstAidModal(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default HealthWellness;

